import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Backdrop,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  LocalOffer as PromoIcon,
} from '@mui/icons-material';
import { getPromoCodes, addPromoCode, updatePromoCode, deletePromoCode } from '../api/adminPanelApi';

const PROMO_TYPES = [
  { value: 'fixed_amount', label: 'Fixed Amount (INR)' },
  { value: 'percent', label: 'Percent (%)' },
  { value: 'referral', label: 'Referral' },
];

const BORNE_BY = [
  { value: 'hotel', label: 'Hotel' },
  { value: 'tabtrips', label: 'TabTrips' },
];

const formatCurrency = (amount) => {
  if (amount == null) return '–';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount);
};

const formatDateTime = (str) => {
  if (!str) return '–';
  return new Date(str).toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const emptyForm = () => ({
  code: '',
  type: 'fixed_amount',
  value: '',
  discount_borne_by: 'tabtrips',
  min_booking_amount: '',
  max_discount_cap: '',
  max_uses_total: '',
  max_uses_per_user: 1,
  valid_from: '',
  valid_to: '',
  is_active: true,
  referral_id: '',
  description: '',
});

const PromoCodes = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await getPromoCodes();
      if (res.success && res.data?.data) {
        setList(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setSnackbar({ open: true, message: res.error || 'Failed to load promo codes', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to load', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleAddOpen = () => {
    setForm(emptyForm());
    setAddOpen(true);
  };

  const handleAddClose = () => {
    setAddOpen(false);
    setSubmitLoading(false);
  };

  const handleEditOpen = (row) => {
    setEditingId(row.id);
    setForm({
      code: row.code || '',
      type: row.type || 'fixed_amount',
      value: row.value ?? '',
      discount_borne_by: row.discount_borne_by || 'tabtrips',
      min_booking_amount: row.min_booking_amount ?? '',
      max_discount_cap: row.max_discount_cap ?? '',
      max_uses_total: row.max_uses_total ?? '',
      max_uses_per_user: row.max_uses_per_user ?? 1,
      valid_from: row.valid_from ? row.valid_from.slice(0, 19).replace(' ', 'T') : '',
      valid_to: row.valid_to ? row.valid_to.slice(0, 19).replace(' ', 'T') : '',
      is_active: !!row.is_active,
      referral_id: row.referral_id || '',
      description: row.description || '',
    });
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditingId(null);
    setSubmitLoading(false);
  };

  const handleDeleteOpen = (row) => {
    setDeleteTarget(row);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setDeleteTarget(null);
    setSubmitLoading(false);
  };

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const buildPayload = (isEdit) => {
    const p = {
      code: (form.code || '').trim().toUpperCase(),
      type: form.type,
      value: Number(form.value) || 0,
      discount_borne_by: form.discount_borne_by,
      min_booking_amount: form.min_booking_amount === '' || form.min_booking_amount == null ? null : Number(form.min_booking_amount),
      max_discount_cap: form.max_discount_cap === '' || form.max_discount_cap == null ? null : Number(form.max_discount_cap),
      max_uses_total: form.max_uses_total === '' || form.max_uses_total == null ? null : Number(form.max_uses_total),
      max_uses_per_user: Number(form.max_uses_per_user) || 0,
      valid_from: form.valid_from ? form.valid_from.replace('T', ' ') + ':00' : null,
      valid_to: form.valid_to ? form.valid_to.replace('T', ' ') + ':00' : null,
      is_active: !!form.is_active,
      referral_id: (form.referral_id || '').trim() || null,
      description: (form.description || '').trim() || null,
    };
    if (isEdit) p.id = editingId;
    return p;
  };

  const validateAdd = () => {
    if (!(form.code || '').trim()) {
      setSnackbar({ open: true, message: 'Code is required', severity: 'warning' });
      return false;
    }
    if (!['fixed_amount', 'percent', 'referral'].includes(form.type)) {
      setSnackbar({ open: true, message: 'Invalid type', severity: 'warning' });
      return false;
    }
    const v = Number(form.value);
    if (isNaN(v) || v < 0) {
      setSnackbar({ open: true, message: 'Value must be >= 0', severity: 'warning' });
      return false;
    }
    if (!['hotel', 'tabtrips'].includes(form.discount_borne_by)) {
      setSnackbar({ open: true, message: 'Invalid discount borne by', severity: 'warning' });
      return false;
    }
    return true;
  };

  const handleAddSubmit = async () => {
    if (!validateAdd()) return;
    setSubmitLoading(true);
    try {
      const payload = buildPayload(false);
      const res = await addPromoCode(payload);
      if (res.success) {
        setSnackbar({ open: true, message: 'Promo code created.', severity: 'success' });
        handleAddClose();
        fetchList();
      } else {
        setSnackbar({ open: true, message: res.error || 'Failed to add', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to add', severity: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!editingId) return;
    if (!(form.code || '').trim()) {
      setSnackbar({ open: true, message: 'Code is required', severity: 'warning' });
      return;
    }
    setSubmitLoading(true);
    try {
      const payload = buildPayload(true);
      const res = await updatePromoCode(payload);
      if (res.success) {
        setSnackbar({ open: true, message: 'Promo code updated.', severity: 'success' });
        handleEditClose();
        fetchList();
      } else {
        setSnackbar({ open: true, message: res.error || 'Failed to update', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to update', severity: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleToggleActive = async (row) => {
    try {
      const res = await updatePromoCode({ id: row.id, is_active: !row.is_active });
      if (res.success) {
        setSnackbar({ open: true, message: row.is_active ? 'Promo deactivated.' : 'Promo activated.', severity: 'success' });
        fetchList();
      } else {
        setSnackbar({ open: true, message: res.error || 'Failed to update', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to update', severity: 'error' });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setSubmitLoading(true);
    try {
      const res = await deletePromoCode(deleteTarget.id);
      if (res.success) {
        setSnackbar({ open: true, message: 'Promo code deleted.', severity: 'success' });
        handleDeleteClose();
        fetchList();
      } else {
        const msg = res.error || 'Failed to delete';
        const suggestDeactivate = /used by \d+ booking/i.test(msg);
        setSnackbar({
          open: true,
          message: suggestDeactivate ? `${msg} Deactivate it instead (toggle Active off).` : msg,
          severity: 'error',
        });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to delete', severity: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const labelShrink = { shrink: true };

  const renderFormFields = () => (
    <Grid container spacing={2.5} sx={{ mt: 0 }}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Code"
          value={form.code}
          onChange={(e) => setField('code', e.target.value)}
          placeholder="e.g. WELCOME100"
          InputLabelProps={labelShrink}
          inputProps={{ style: { textTransform: 'uppercase' } }}
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth variant="outlined" size="medium">
          <InputLabel id="promo-type-label" shrink>Type</InputLabel>
          <Select value={form.type} label="Type" labelId="promo-type-label" onChange={(e) => setField('type', e.target.value)}>
            {PROMO_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={form.type === 'percent' ? 'Percentage (e.g. 10 for 10%)' : 'Value (INR)'}
          type="number"
          value={form.value}
          onChange={(e) => setField('value', e.target.value)}
          InputLabelProps={labelShrink}
          inputProps={{ min: 0, step: form.type === 'percent' ? 1 : 0.01 }}
          InputProps={form.type !== 'percent' ? { startAdornment: <InputAdornment position="start">₹</InputAdornment> } : undefined}
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth variant="outlined" size="medium">
          <InputLabel id="promo-borne-label" shrink>Discount borne by</InputLabel>
          <Select value={form.discount_borne_by} label="Discount borne by" labelId="promo-borne-label" onChange={(e) => setField('discount_borne_by', e.target.value)}>
            {BORNE_BY.map((b) => (
              <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Min booking amount (INR)"
          type="number"
          value={form.min_booking_amount}
          onChange={(e) => setField('min_booking_amount', e.target.value)}
          InputLabelProps={labelShrink}
          inputProps={{ min: 0 }}
          placeholder="Optional"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Max discount cap (INR) – for percent"
          type="number"
          value={form.max_discount_cap}
          onChange={(e) => setField('max_discount_cap', e.target.value)}
          InputLabelProps={labelShrink}
          inputProps={{ min: 0 }}
          placeholder="Optional, recommended for %"
          helperText={form.type === 'percent' ? 'Discount = booking × value/100, capped by this' : ''}
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Max uses total"
          type="number"
          value={form.max_uses_total}
          onChange={(e) => setField('max_uses_total', e.target.value)}
          InputLabelProps={labelShrink}
          inputProps={{ min: 0 }}
          placeholder="Empty = unlimited"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Max uses per user"
          type="number"
          value={form.max_uses_per_user}
          onChange={(e) => setField('max_uses_per_user', e.target.value)}
          InputLabelProps={labelShrink}
          inputProps={{ min: 0 }}
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Valid from"
          type="datetime-local"
          value={form.valid_from}
          onChange={(e) => setField('valid_from', e.target.value)}
          InputLabelProps={labelShrink}
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Valid to"
          type="datetime-local"
          value={form.valid_to}
          onChange={(e) => setField('valid_to', e.target.value)}
          InputLabelProps={labelShrink}
          variant="outlined"
        />
      </Grid>
      {form.type === 'referral' && (
        <Grid item xs={12}>
          <TextField fullWidth label="Referral ID" value={form.referral_id} onChange={(e) => setField('referral_id', e.target.value)} placeholder="Optional" InputLabelProps={labelShrink} variant="outlined" />
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField fullWidth label="Description (admin note)" value={form.description} onChange={(e) => setField('description', e.target.value)} multiline rows={2} placeholder="Optional" InputLabelProps={labelShrink} variant="outlined" />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel control={<Switch checked={!!form.is_active} onChange={(e) => setField('is_active', e.target.checked)} />} label="Active" />
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/')} sx={{ color: '#FF7700', '&:hover': { backgroundColor: 'rgba(255, 119, 0, 0.1)' } }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>Promo Codes</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchList} disabled={loading} sx={{ borderColor: '#FF7700', color: '#FF7700' }}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddOpen} sx={{ backgroundColor: '#FF7700', '&:hover': { backgroundColor: '#e65100' } }}>Add Promo Code</Button>
        </Box>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>Create and manage checkout promo codes. Percent promos: set a max discount cap (INR) when possible.</Typography>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Code</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell align="right"><strong>Value</strong></TableCell>
              <TableCell><strong>Borne by</strong></TableCell>
              <TableCell align="right"><strong>Min (₹)</strong></TableCell>
              <TableCell align="right"><strong>Cap (₹)</strong></TableCell>
              <TableCell align="right"><strong>Uses</strong></TableCell>
              <TableCell><strong>Valid from</strong></TableCell>
              <TableCell><strong>Valid to</strong></TableCell>
              <TableCell><strong>Active</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length === 0 && !loading && (
              <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4 }}>No promo codes. Add one to get started.</TableCell></TableRow>
            )}
            {list.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{row.code}</TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell align="right">{row.type === 'percent' ? `${row.value}%` : formatCurrency(row.value)}</TableCell>
                <TableCell>{row.discount_borne_by}</TableCell>
                <TableCell align="right">{formatCurrency(row.min_booking_amount)}</TableCell>
                <TableCell align="right">{formatCurrency(row.max_discount_cap)}</TableCell>
                <TableCell align="right">{row.usage_count != null ? `${row.usage_count}${row.max_uses_total != null ? ` / ${row.max_uses_total}` : ''}` : '–'}</TableCell>
                <TableCell>{formatDateTime(row.valid_from)}</TableCell>
                <TableCell>{formatDateTime(row.valid_to)}</TableCell>
                <TableCell>
                  <Chip label={row.is_active ? 'Active' : 'Inactive'} size="small" color={row.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="center">
                  <Button size="small" variant="outlined" onClick={() => handleToggleActive(row)} sx={{ mr: 0.5, minWidth: 72 }}>
                    {row.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <IconButton size="small" onClick={() => handleEditOpen(row)} title="Edit"><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleDeleteOpen(row)} title="Delete" sx={{ color: '#f44336' }}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Dialog */}
      <Dialog open={addOpen} onClose={handleAddClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 1, py: 2, px: 3 }}>
          <PromoIcon sx={{ color: '#FF7700' }} /> Add Promo Code
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 3, px: 3, overflow: 'visible' }}>
          {renderFormFields()}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee' }}>
          <Button onClick={handleAddClose}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSubmit} disabled={submitLoading} sx={{ backgroundColor: '#FF7700' }}>
            {submitLoading ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 1, py: 2, px: 3 }}>
          <EditIcon sx={{ color: '#FF7700' }} /> Edit Promo Code
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 3, px: 3, overflow: 'visible' }}>
          {renderFormFields()}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee' }}>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={submitLoading} sx={{ backgroundColor: '#FF7700' }}>
            {submitLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete promo code?</DialogTitle>
        <DialogContent>
          {deleteTarget && (
            <Typography>Delete &quot;{deleteTarget.code}&quot;? This is only allowed if it has never been used. If it has been used, deactivate it instead.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={submitLoading}>
            {submitLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PromoCodes;
